import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Link2,
  Lock,
  Package,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import {
  bindMaterial1C,
  createBomItem,
  createMaterial,
  createMaterialFrom1C,
  createOutput,
  deleteBomItem,
  deleteOutput,
  fetch1CImageBlobUrl,
  getRollup,
  listMaterials,
  listOutputs,
  refreshMaterialPrice1C,
  refreshProjectPrices1C,
  searchNomenclature1C,
  updateBomItem,
  updateMaterial,
  type BomItemView,
  type BomOutputView,
  type MaterialView,
  type OneCSearchResult,
  type PdevMeta,
  type RollupRoot,
} from "@/api/product-dev";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { OneCSearchPopover } from "@/pages/product-dev/OneCSearch";
import { OfferHistory } from "@/pages/product-dev/offers";
import { PEREDEL_HINT, PEREDEL_LABEL, PEREDEL_STYLES, apiErr, money, num } from "@/pages/product-dev/format";

const selectCls =
  "h-9 rounded-md border border-input bg-transparent px-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring";

// Порядок и подписи «что производим» — человеческим языком (прячем ПФ/ПГП/ГП).
const PEREDEL_ORDER = ["GP", "PGP", "PF"] as const;

interface Props {
  projectId: string;
  meta: PdevMeta;
  locked: boolean;
}

export function BomTreePanel({ projectId, meta, locked }: Props): React.ReactElement {
  const qc = useQueryClient();
  const outputs = useQuery({
    queryKey: ["pdev", "outputs", projectId],
    queryFn: () => listOutputs(projectId),
  });
  const rollup = useQuery({
    queryKey: ["pdev", "rollup", projectId],
    queryFn: () => getRollup(projectId),
  });

  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey: ["pdev", "outputs", projectId] });
    qc.invalidateQueries({ queryKey: ["pdev", "rollup", projectId] });
    qc.invalidateQueries({ queryKey: ["pdev", "project", projectId] });
    qc.invalidateQueries({ queryKey: ["pdev", "materials"] });
  };

  const refreshPrices = useMutation({
    mutationFn: () => refreshProjectPrices1C(projectId),
    onSuccess: (r) => {
      invalidate();
      const parts: string[] = [];
      if (r.updated) parts.push(`обновлено ${r.updated}`);
      if (r.no_receipt) parts.push(`без приходов ${r.no_receipt}`);
      if (r.skipped_offer) parts.push(`оффер ${r.skipped_offer}`);
      if (r.not_bound) parts.push(`не из 1С ${r.not_bound}`);
      toast.success(`Цены из 1С — ${parts.join(", ") || "нечего обновлять"}`);
    },
    onError: (e) => toast.error(apiErr(e, "Не удалось обновить цены из 1С")),
  });

  if (outputs.isLoading) return <div className="text-sm text-muted-foreground">Загрузка…</div>;
  const items = outputs.data ?? [];

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[1fr_340px]">
      <div className="space-y-4">
        {locked ? (
          <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            <Lock className="size-4" /> Продукт утверждён — состав только для чтения.
          </div>
        ) : (
          <AddProduced projectId={projectId} onDone={invalidate} />
        )}

        {items.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            <Package className="mx-auto mb-2 size-6 opacity-50" />
            Из чего состоит продукт? Начните с «Готового продукта» и добавьте его компоненты
            (флакон, этикетка, крышка, короб), а состав/рецептуру — как «Формулу».
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((o) => (
              <OutputCard
                key={o.id}
                output={o}
                outputs={items}
                projectId={projectId}
                meta={meta}
                locked={locked}
                onChange={invalidate}
              />
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3 lg:sticky lg:top-4">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-semibold">Себестоимость</div>
          {!locked && (
            <Button
              size="sm"
              variant="outline"
              className="h-7"
              onClick={() => refreshPrices.mutate()}
              disabled={refreshPrices.isPending}
              title="Подтянуть цены последних закупок из 1С"
            >
              <RefreshCw className={`mr-1 size-3.5 ${refreshPrices.isPending ? "animate-spin" : ""}`} />
              Цены из 1С
            </Button>
          )}
        </div>
        {(rollup.data?.roots ?? []).map((r) => (
          <CostCard key={r.output_id} root={r} />
        ))}
        {rollup.data && rollup.data.roots.length === 0 && (
          <div className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground">
            Появится, когда добавите компоненты с ценами
          </div>
        )}
      </div>
    </div>
  );
}

// ── «Что производим» — добавление в человеческих терминах ──────────────────────
function AddProduced({
  projectId,
  onDone,
}: {
  projectId: string;
  onDone: () => void;
}): React.ReactElement {
  const [peredel, setPeredel] = useState<string>("GP");
  const [title, setTitle] = useState("");

  const create = useMutation({
    mutationFn: () => createOutput(projectId, { peredel, channel: null, title: title.trim() || null }),
    onSuccess: () => {
      setTitle("");
      onDone();
    },
    onError: (e) => toast.error(apiErr(e, "Не удалось добавить")),
  });

  return (
    <form
      className="flex flex-wrap items-end gap-2 rounded-lg border bg-card p-3"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <div className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">Что добавляем</span>
        <select className={selectCls} value={peredel} onChange={(e) => setPeredel(e.target.value)}>
          {PEREDEL_ORDER.map((p) => (
            <option key={p} value={p}>
              {PEREDEL_LABEL[p]} — {PEREDEL_HINT[p]}
            </option>
          ))}
        </select>
      </div>
      <Input
        placeholder="Название (напр. «Гель для душа Роза»)…"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-72"
      />
      <Button type="submit" variant="outline" disabled={create.isPending}>
        <Plus className="mr-1 size-4" /> Добавить
      </Button>
    </form>
  );
}

// ── Карточка производимого + его состав ───────────────────────────────────────
function OutputCard({
  output,
  outputs,
  projectId,
  meta,
  locked,
  onChange,
}: {
  output: BomOutputView;
  outputs: BomOutputView[];
  projectId: string;
  meta: PdevMeta;
  locked: boolean;
  onChange: () => void;
}): React.ReactElement {
  const addChild = useMutation({
    mutationFn: (childId: string) =>
      createBomItem({ output_id: output.id, child_output_id: childId, qty: 1 }),
    onSuccess: onChange,
    onError: (e) => toast.error(apiErr(e, "Не удалось добавить")),
  });
  const delOutput = useMutation({
    mutationFn: () => deleteOutput(output.id),
    onSuccess: onChange,
    onError: (e) => toast.error(apiErr(e, "Не удалось удалить")),
  });

  const others = outputs.filter((o) => o.id !== output.id);

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${PEREDEL_STYLES[output.peredel] ?? ""}`}>
          {PEREDEL_LABEL[output.peredel] ?? output.peredel}
        </span>
        <span className="font-medium">{output.title ?? "(без названия)"}</span>
        {output.channel && (
          <span className="text-xs text-muted-foreground">· {meta.channels[output.channel]}</span>
        )}
        {!locked && (
          <Button size="icon" variant="ghost" className="ml-auto size-7" onClick={() => delOutput.mutate()}>
            <Trash2 className="size-4 text-muted-foreground hover:text-destructive" />
          </Button>
        )}
      </div>

      <div className="divide-y">
        {output.items.map((it) => (
          <ItemRow key={it.id} item={it} projectId={projectId} locked={locked} onChange={onChange} />
        ))}
        {output.items.length === 0 && (
          <div className="px-3 py-2 text-xs text-muted-foreground">Пока пусто — добавьте компоненты</div>
        )}
      </div>

      {!locked && (
        <div className="flex flex-wrap items-center gap-2 border-t px-3 py-2">
          <AddComponent outputId={output.id} onChange={onChange} />
          {others.length > 0 && (
            <ChildPicker outputs={others} onPick={(id) => addChild.mutate(id)} />
          )}
        </div>
      )}
    </div>
  );
}

// ── Добавление компонента: из 1С (дебаунс-поиск) / из каталога / создать новый ─
function AddComponent({
  outputId,
  onChange,
}: {
  outputId: string;
  onChange: () => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [dq, setDq] = useState(""); // дебаунс-значение для 1С-поиска
  const [nprice, setNprice] = useState("");

  // Дебаунс: не дёргаем 1С на каждый символ.
  useEffect(() => {
    const t = setTimeout(() => setDq(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const oneC = useQuery({
    queryKey: ["pdev", "1c-nom", dq],
    queryFn: () => searchNomenclature1C(dq),
    enabled: open && dq.length >= 2,
    retry: false,
    staleTime: 60_000,
  });

  const materials = useQuery({
    queryKey: ["pdev", "materials"],
    queryFn: () => listMaterials(),
    enabled: open,
  });
  const rows = (materials.data ?? []).filter(
    (m) => !q || m.display_name.toLowerCase().includes(q.toLowerCase()),
  );

  const finish = (msg: string) => {
    setOpen(false);
    setQ("");
    setNprice("");
    onChange();
    toast.success(msg);
  };

  const addExisting = useMutation({
    mutationFn: (m: MaterialView) => createBomItem({ output_id: outputId, material_id: m.id, qty: 1 }),
    onSuccess: () => finish("Компонент добавлен"),
    onError: (e) => toast.error(apiErr(e, "Не удалось добавить")),
  });

  // Из 1С: создаём материал, привязанный к номенклатуре, и сразу строку состава.
  const addFrom1C = useMutation({
    mutationFn: async (r: OneCSearchResult) => {
      const m = await createMaterialFrom1C({
        one_c_ref_key: r.ref_key,
        name: r.name ?? "Из 1С",
        material_type: "other",
      });
      await createBomItem({ output_id: outputId, material_id: m.id, qty: 1 });
    },
    onSuccess: () => finish("Компонент из 1С добавлен"),
    onError: (e) => toast.error(apiErr(e, "Не удалось добавить из 1С")),
  });

  // Инлайн-создание вручную: новый материал каталога + сразу строка состава.
  const createAndAdd = useMutation({
    mutationFn: async () => {
      const m = await createMaterial({
        local_name: q.trim(),
        material_type: "other",
        price: nprice === "" ? null : Number(nprice),
      });
      await createBomItem({ output_id: outputId, material_id: m.id, qty: 1 });
    },
    onSuccess: () => finish("Компонент создан и добавлен"),
    onError: (e) => toast.error(apiErr(e, "Не удалось создать")),
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="mr-1 size-4" /> Компонент
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[30rem] p-2" align="start">
        <Input
          autoFocus
          placeholder="Название или артикул…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="mb-2 h-8"
        />

        {/* Из 1С — дебаунс-поиск с миниатюрой */}
        {dq.length >= 2 && (
          <div className="mb-2">
            <div className="flex items-center gap-1 px-1 text-[11px] uppercase text-muted-foreground">
              Из 1С {oneC.isFetching && <span className="normal-case">· ищем…</span>}
            </div>
            {oneC.isError && (
              <div className="flex items-start gap-1 px-1 py-1 text-xs text-destructive">
                <AlertTriangle className="mt-0.5 size-3 shrink-0" />
                {apiErr(oneC.error, "Ошибка поиска в 1С")}
              </div>
            )}
            <div className="max-h-56 space-y-0.5 overflow-y-auto">
              {(oneC.data ?? []).map((r) => (
                <div
                  key={r.ref_key}
                  role="button"
                  tabIndex={0}
                  className="flex w-full cursor-pointer items-start gap-2 rounded px-1.5 py-1 hover:bg-accent"
                  onClick={() => addFrom1C.mutate(r)}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && addFrom1C.mutate(r)}
                >
                  {r.image_file_ref ? (
                    <OneCImage fileRef={r.image_file_ref} className="size-11 shrink-0 rounded border" />
                  ) : (
                    <div className="grid size-11 shrink-0 place-items-center rounded bg-muted text-muted-foreground">
                      <Package className="size-4" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="line-clamp-2 text-sm leading-tight">{r.name ?? "(без названия)"}</div>
                    {r.article && (
                      <div className="truncate text-xs text-muted-foreground">арт. {r.article}</div>
                    )}
                  </div>
                </div>
              ))}
              {oneC.data?.length === 0 && !oneC.isFetching && (
                <div className="px-1 py-1 text-xs text-muted-foreground">В 1С не найдено</div>
              )}
            </div>
          </div>
        )}

        {/* Из каталога портала */}
        {rows.length > 0 && (
          <div className="mb-2 max-h-40 space-y-0.5 overflow-y-auto">
            <div className="px-1 text-[11px] uppercase text-muted-foreground">Из каталога</div>
            {rows.map((m) => (
              <button
                key={m.id}
                type="button"
                className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
                onClick={() => addExisting.mutate(m)}
              >
                <span className="truncate">{m.display_name}</span>
                <span className="ml-2 shrink-0 text-xs text-muted-foreground">
                  {money(m.price, m.currency)}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Создать новый вручную */}
        <div className="rounded-md bg-muted/40 p-2">
          <div className="mb-1 text-[11px] uppercase text-muted-foreground">
            Создать новый {q.trim() ? `«${q.trim()}»` : "вручную"}
          </div>
          <div className="flex items-center gap-1">
            <Input
              placeholder="цена ₽ (необязательно)"
              value={nprice}
              onChange={(e) => setNprice(e.target.value)}
              className="h-8 flex-1"
              inputMode="decimal"
            />
            <Button
              size="sm"
              className="shrink-0"
              disabled={!q.trim() || createAndAdd.isPending}
              onClick={() => createAndAdd.mutate()}
            >
              Создать
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Миниатюра картинки номенклатуры 1С: тянет blob под аутентификацией; `object-contain`
 * (видно целиком, без обрезки); по наведению — большое превью в портале (не режется
 * скроллом списка). Нет картинки / в томах (404) — тихо заглушка. */
function OneCImage({ fileRef, className }: { fileRef: string; className?: string }): React.ReactElement {
  const [src, setSrc] = useState<string | null>(null);
  const [hover, setHover] = useState(false);
  useEffect(() => {
    let alive = true;
    let url: string | null = null;
    fetch1CImageBlobUrl(fileRef)
      .then((u) => {
        url = u;
        if (alive) setSrc(u);
        else URL.revokeObjectURL(u);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [fileRef]);

  const box = (
    <div
      className={cn("overflow-hidden bg-card dark:bg-muted", className)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {src ? (
        <img src={src} alt="" className="size-full object-contain" />
      ) : (
        <div className="size-full animate-pulse bg-muted" />
      )}
    </div>
  );

  if (!src) return box;
  return (
    <Popover open={hover} onOpenChange={() => undefined}>
      <PopoverTrigger asChild>{box}</PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={8}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-auto rounded-md bg-popover p-1 shadow-lg"
      >
        <img
          src={src}
          alt=""
          className="max-h-80 max-w-80 rounded object-contain"
        />
      </PopoverContent>
    </Popover>
  );
}

function ChildPicker({
  outputs,
  onPick,
}: {
  outputs: BomOutputView[];
  onPick: (id: string) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="mr-1 size-4" /> Содержит…
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-1" align="start">
        <div className="px-2 py-1 text-[11px] uppercase text-muted-foreground">Вложить в состав</div>
        {outputs.map((o) => (
          <button
            key={o.id}
            type="button"
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
            onClick={() => {
              onPick(o.id);
              setOpen(false);
            }}
          >
            <span className={`rounded px-1 text-xs ${PEREDEL_STYLES[o.peredel] ?? ""}`}>
              {PEREDEL_LABEL[o.peredel] ?? o.peredel}
            </span>
            <span className="truncate">{o.title ?? "(без названия)"}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function ItemRow({
  item,
  projectId,
  locked,
  onChange,
}: {
  item: BomItemView;
  projectId: string;
  locked: boolean;
  onChange: () => void;
}): React.ReactElement {
  const [qty, setQty] = useState(item.qty.toString());
  const [open, setOpen] = useState(false);
  const isMaterial = item.ref_kind === "material" && !!item.material_id;

  const save = useMutation({
    mutationFn: () => updateBomItem(item.id, { qty: Number(qty) }),
    onSuccess: onChange,
    onError: (e) => toast.error(apiErr(e, "Не удалось сохранить")),
  });
  const del = useMutation({
    mutationFn: () => deleteBomItem(item.id),
    onSuccess: onChange,
    onError: (e) => toast.error(apiErr(e, "Не удалось удалить")),
  });

  return (
    <div>
      <div className="flex items-center gap-2 px-3 py-1.5 text-sm">
        {isMaterial ? (
          <button
            type="button"
            className="flex items-center gap-1 text-left"
            onClick={() => setOpen((s) => !s)}
            title="Цена, предложения поставщиков, 1С"
          >
            {open ? (
              <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
            )}
            {item.ref_name}
          </button>
        ) : (
          <span className="font-medium">Содержит: {item.ref_name}</span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {locked ? (
            <span className="text-muted-foreground">
              × {num(item.qty)} {item.unit}
            </span>
          ) : (
            <>
              <Input
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                onBlur={() => Number(qty) !== item.qty && save.mutate()}
                onKeyDown={(e) => e.key === "Enter" && save.mutate()}
                className="h-7 w-20 text-right"
                inputMode="decimal"
                title="Количество на 1 продукт"
              />
              <span className="w-8 text-xs text-muted-foreground">{item.unit}</span>
              <Button size="icon" variant="ghost" className="size-7" onClick={() => del.mutate()}>
                <Trash2 className="size-4 text-muted-foreground hover:text-destructive" />
              </Button>
            </>
          )}
        </div>
      </div>
      {open && isMaterial && item.material_id && (
        <div className="border-t bg-muted/20 px-3 py-2">
          <ComponentDetail materialId={item.material_id} projectId={projectId} locked={locked} />
        </div>
      )}
    </div>
  );
}

/** Раскрытие компонента: цена (правка вручную), привязка к 1С и история цен от
 * поставщиков (добавить предложение + выбрать утверждённую). Заменяет отдельные
 * вкладки «Каталог» и «Переговоры и цены» — всё в контексте продукта. */
function ComponentDetail({
  materialId,
  projectId,
  locked,
}: {
  materialId: string;
  projectId: string;
  locked: boolean;
}): React.ReactElement {
  const qc = useQueryClient();
  const materials = useQuery({ queryKey: ["pdev", "materials"], queryFn: () => listMaterials() });
  const m = (materials.data ?? []).find((x) => x.id === materialId);
  const [price, setPrice] = useState<string>("");
  useEffect(() => {
    setPrice(m?.price != null ? String(m.price) : "");
  }, [m?.price]);

  const savePrice = useMutation({
    mutationFn: () => updateMaterial(materialId, { price: price === "" ? null : Number(price) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pdev", "materials"] }),
    onError: (e) => toast.error(apiErr(e, "Не удалось сохранить цену")),
  });
  const bind = useMutation({
    mutationFn: (r: OneCSearchResult) =>
      bindMaterial1C(materialId, {
        one_c_kind: "nomenclature",
        one_c_ref_key: r.ref_key,
        display_name: r.name ?? undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pdev", "materials"] });
      toast.success("Привязан к номенклатуре 1С");
    },
    onError: (e) => toast.error(apiErr(e, "Не удалось привязать")),
  });
  const refreshPrice = useMutation({
    mutationFn: () => refreshMaterialPrice1C(materialId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pdev", "materials"] });
      qc.invalidateQueries({ queryKey: ["pdev", "rollup", projectId] });
      toast.success("Цена обновлена из 1С");
    },
    onError: (e) => toast.error(apiErr(e, "Не удалось обновить из 1С")),
  });

  if (!m) return <div className="text-xs text-muted-foreground">Загрузка…</div>;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Цена:</span>
          {locked ? (
            <span>{money(m.price, m.currency)}</span>
          ) : (
            <>
              <Input
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                onBlur={() => String(m.price ?? "") !== price && savePrice.mutate()}
                onKeyDown={(e) => e.key === "Enter" && savePrice.mutate()}
                className="h-7 w-28"
                inputMode="decimal"
                placeholder="₽"
              />
              {m.price_source === "portal_offer" && (
                <span className="text-xs text-muted-foreground">из предложения</span>
              )}
              {m.onec.is_bound && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 gap-1 px-2 text-xs"
                  onClick={() => refreshPrice.mutate()}
                  disabled={refreshPrice.isPending}
                  title="Взять цену последней закупки из 1С"
                >
                  <RefreshCw className={`size-3 ${refreshPrice.isPending ? "animate-spin" : ""}`} /> из 1С
                </Button>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">1С:</span>
          {m.onec.is_bound ? (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
              <Link2 className="size-3.5" /> привязан
            </span>
          ) : locked ? (
            <span className="text-xs text-muted-foreground">не привязан</span>
          ) : (
            <OneCSearchPopover
              kind="nomenclature"
              onPick={(r) => bind.mutate(r)}
              trigger={
                <Button size="sm" variant="outline" className="h-7">
                  Привязать 1С
                </Button>
              }
            />
          )}
        </div>
      </div>
      {m.price_detail && (
        <div className="text-xs text-muted-foreground">{m.price_detail}</div>
      )}
      <div>
        <div className="mb-1 text-[11px] uppercase text-muted-foreground">Цены от поставщиков</div>
        <OfferHistory materialId={materialId} projectId={projectId} />
      </div>
    </div>
  );
}

function CostCard({ root }: { root: RollupRoot }): React.ReactElement {
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{root.display_name}</span>
        <span className="text-base font-semibold">
          {root.total_cost == null ? "—" : money(root.total_cost, root.currency)}
        </span>
      </div>
      {root.warnings.map((w) => (
        <div key={w} className="mt-1 flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300">
          <AlertTriangle className="size-3.5 shrink-0" /> {w}
        </div>
      ))}
      <table className="mt-2 w-full text-xs">
        <tbody>
          {root.lines.map((ln, i) => (
            <tr key={i} className="border-t align-top">
              <td className="py-1 pr-2">{ln.material_name}</td>
              <td className="whitespace-nowrap py-1 text-right tabular-nums text-muted-foreground">
                {num(ln.qty_total)} {ln.unit}
              </td>
              <td className="whitespace-nowrap py-1 pl-2 text-right tabular-nums">
                {ln.has_price ? (
                  money(ln.line_cost, ln.currency ?? "RUB")
                ) : (
                  <span className="text-amber-600 dark:text-amber-400">нет цены</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
